import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRuntimeConfig } from "@/contexts/runtime-config";
import { isAutofillEnabled } from "@/dev/console/autofill-preference";
import { getMockApplicantContact } from "@/dev/console/data/mock-contact-values";
import {
	type ContactApplicantFields,
	useContactApplicantSchema,
	useValidationCountry,
} from "@/hooks/form/use-contact-field-schemas";
import { useAppCountry, useCountrySettings } from "@/hooks/use-app-country";
import { Applicant, MaritalStatus } from "@/types/form";

export type PersonalInformationForm = {
	applicant: Applicant;
};

export type { ContactApplicantFields };

export const useCivilApplicantSchema = () => {
	const t = useTranslations("forms.errors.general");
	const { asksChildren } = useCountrySettings();

	const baseSchema = z.object({
		maritalStatus: z.nativeEnum(MaritalStatus, { message: t("required") }),
	});

	if (asksChildren) {
		return baseSchema.extend({
			numberOfChildrenBelow18: z
				.number({ message: t("required") })
				.int()
				.min(0)
				.max(100),
		}) satisfies z.ZodType<
			Pick<Applicant, "maritalStatus" | "numberOfChildrenBelow18">
		>;
	}

	return baseSchema satisfies z.ZodType<Pick<Applicant, "maritalStatus">>;
};

const useApplicantZodSchema = () => {
	const contactSchema = useContactApplicantSchema();
	const civilSchema = useCivilApplicantSchema();

	return contactSchema.and(civilSchema) as unknown as z.ZodSchema<Applicant>;
};

export const usePersonalInformationForm = () => {
	const person = useApplicantZodSchema();
	const defaultPhoneCountry = useValidationCountry();
	const country = useAppCountry();
	const defaultApplicant = isAutofillEnabled(useRuntimeConfig().devConsole)
		? getMockApplicantContact(country)
		: undefined;

	const PersonalInfoSchema: z.ZodSchema<PersonalInformationForm> = z.object({
		applicant: person,
	});

	return {
		useForm: useForm<PersonalInformationForm>({
			resolver: zodResolver(
				PersonalInfoSchema as unknown as z.ZodType<
					PersonalInformationForm,
					PersonalInformationForm
				>
			),
			mode: "onTouched",
			defaultValues: {
				applicant: {
					mobileCountry: defaultPhoneCountry,
					...(defaultApplicant ?? {}),
				},
			},
		}),
		schema: PersonalInfoSchema,
	};
};

// Re-export for existing imports.
export { useContactApplicantSchema } from "@/hooks/form/use-contact-field-schemas";
